import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { leaveCalendarLeaveTypeI18nKey } from "./leaveCalendar.constants";

export interface LeaveCalendarLegendProps {
  /**
   * The full company calendar shows every rule (holidays, weekends,
   * rejected/cancelled…). Other views that only ever paint approved /
   * pending leave days (e.g. the employee card's wall calendar) pass
   * `compact` to drop the entries that cannot occur there.
   */
  compact?: boolean;
}

/** The legend behind a button, for a phone where an open legend would leave the calendar a few rows. */
export function LeaveCalendarLegendToggle() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-h-11 w-full items-center justify-between rounded-2xl border border-slate-100 bg-white/60 px-3 text-sm font-medium text-slate-600 shadow-sm"
      >
        {t("leave_calendar_legend")}
        <ChevronDown className={isOpen ? "h-4 w-4 rotate-180" : "h-4 w-4"} />
      </button>
      {isOpen && (
        <div className="mt-2">
          <LeaveCalendarLegend />
        </div>
      )}
    </div>
  );
}

/**
 * Leave-calendar rules legend. Single source of truth for the colours /
 * labels shown on LeaveCalendarPage and reused as-is (subset via `compact`)
 * anywhere else leave days are painted the same way.
 */
export function LeaveCalendarLegend({
  compact = false,
}: LeaveCalendarLegendProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/60 p-2 text-[9px] max-lg:text-xs text-slate-500 backdrop-blur-md shadow-sm">
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
        {/* Vacation – emerald glass cell */}
        <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded bg-emerald-500/85 text-[8px] font-black text-white shadow-sm border border-emerald-400/20">
            V
          </span>
          <span className="font-bold text-slate-500">
            {t("leave_calendar_rules_vacation", { defaultValue: "Vacation" })}
          </span>
        </div>
        {/* Remote Work – indigo glass cell */}
        <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded bg-indigo-500/85 text-[8px] font-black text-white shadow-sm border border-indigo-400/20">
            R
          </span>
          <span className="font-bold text-slate-500">
            {t(leaveCalendarLeaveTypeI18nKey("remote work"), {
              defaultValue: "Remote work",
            })}
          </span>
        </div>
        {/* Business Trip – sky glass cell */}
        <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded bg-sky-500/85 text-[8px] font-black text-white shadow-sm border border-sky-400/20">
            K
          </span>
          <span className="font-bold text-slate-500">
            {t("leave_calendar_rules_business_trip", {
              defaultValue: "Business Trip",
            })}
          </span>
        </div>
        {/* Pending – amber glass with amber ring */}
        <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
          <span className="h-3.5 w-3.5 shrink-0 rounded bg-amber-400/85 shadow-sm border border-amber-300/35 ring-1 ring-amber-400/50" />
          <span className="font-bold text-slate-500">
            {t("leave_calendar_rules_pending", {
              defaultValue: "Pending approval",
            })}
          </span>
        </div>
        {!compact && (
          <>
            {/* Rejected – rose glass cell */}
            <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
              <span className="h-3.5 w-3.5 shrink-0 rounded bg-rose-500/85 shadow-sm border border-rose-300/30" />
              <span className="font-bold text-slate-500">
                {t("leave_calendar_rules_rejected_cancelled", {
                  defaultValue: "Rejected / Cancelled",
                })}
              </span>
            </div>
            {/* Holiday dot – amber dot in date header */}
            <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
              <span className="h-3.5 w-3.5 shrink-0 rounded flex items-center justify-center bg-amber-50/30 border border-amber-100/30">
                <span className="inline-block h-1 w-1 rounded-full bg-amber-500" />
              </span>
              <span className="font-bold text-slate-500">
                {t("leave_calendar_rules_holiday_dot", {
                  defaultValue: "Holiday dot",
                })}
              </span>
            </div>
            {/* Holiday bg – amber diagonal stripes */}
            <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
              <span
                className="h-3.5 w-3.5 shrink-0 rounded border border-amber-100/50"
                style={{
                  background:
                    "repeating-linear-gradient(-45deg, #fffbeb, #fffbeb 2px, #fef3c7 2px, #fef3c7 4px)",
                }}
              />
              <span className="font-bold text-slate-500">
                {t("leave_calendar_rules_holiday_bg", {
                  defaultValue: "Holiday day",
                })}
              </span>
            </div>
            {/* Weekend bg – slate diagonal stripes */}
            <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
              <span
                className="h-3.5 w-3.5 shrink-0 rounded border border-slate-200/50"
                style={{
                  background:
                    "repeating-linear-gradient(-45deg, #fafafa, #fafafa 2px, #f1f5f9 2px, #f1f5f9 4px)",
                }}
              />
              <span className="font-bold text-slate-500">
                {t("leave_calendar_rules_weekend_bg", {
                  defaultValue: "Weekend off-day",
                })}
              </span>
            </div>
          </>
        )}
        {/* Today – teal circle number */}
        <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
          <span className="h-3.5 w-3.5 shrink-0 rounded-full bg-teal-600 text-white text-[7px] font-extrabold flex items-center justify-center shadow-sm shadow-teal-600/40 ring-1 ring-teal-100">
            T
          </span>
          <span className="font-bold text-slate-500">
            {t("leave_calendar_rules_today_bg", {
              defaultValue: "Today",
            })}
          </span>
        </div>
        {!compact && (
          <>
            {/* Today + Holiday – teal circle + amber corner badge */}
            <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
              <span
                className="relative h-3.5 w-3.5 shrink-0 rounded overflow-hidden flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #f0fdfa 50%, #fffbeb 50%)",
                  boxShadow: "inset 0 0 0 1px rgba(20,184,166,0.25)",
                }}
              >
                <span className="relative h-3 w-3 rounded-full bg-teal-600 text-white text-[5px] font-extrabold flex items-center justify-center ring-1 ring-teal-100 z-10 shadow-sm">
                  T
                  <span className="absolute -bottom-px -right-px h-1 w-1 rounded-full bg-amber-500 ring-1 ring-white" />
                </span>
              </span>
              <span className="font-bold text-slate-500">
                {t("leave_calendar_rules_today_holiday", {
                  defaultValue: "Today + Holiday",
                })}
              </span>
            </div>
          </>
        )}
        {/* Half-day – bottom half filled */}
        <div className="flex items-center gap-1.5 bg-slate-50/20 p-1 rounded-lg border border-slate-100/30 hover:bg-slate-50 transition-colors">
          <span className="relative h-3.5 w-3.5 overflow-hidden rounded border border-slate-200/50 shrink-0 bg-white">
            <span className="absolute bottom-0 left-0 right-0 h-1/2 bg-emerald-500/85" />
          </span>
          <span className="font-bold text-slate-500">
            {t("leave_calendar_rules_half_day", { defaultValue: "Half-day" })}
          </span>
        </div>
      </div>
    </div>
  );
}
